import { AnyAction, createAsyncThunk, createSlice, Dispatch, isAnyOf, Middleware, PayloadAction, unwrapResult } from '@reduxjs/toolkit';
import { Socket } from 'socket.io-client';
import { RootState } from '../../app/store';
import { FixedQueue, pushInFixedQueue, removeFromFixedQueue } from '../../app/utils';
import { UserDetails } from '../account/AccountAPI';
import { getUser, logout } from '../account/AccountSlice';
import { Group } from '../groups/GroupsAPI';
import { loadGroups } from '../groups/GroupsSlice';
import { Project, ProjectEntity } from '../projects/ProjectsAPI';
import { initEnvironment, loadMine } from '../projects/ProjectsSlice';
import { createTransform, persistReducer } from 'redux-persist';
import {
  fetchChatMessages as fetchChatMessagesAPI,
  sendChatMessage as sendChatMessageAPI,
  openChatConnection as openChatConnectionAPI,
  ReceivedChatMessage,
  RoomId,
  ChatMessageRequest,
  ServerRoomId,
  Room,
  ServerRoom,
  RoomManager,
  MessagesStorage,
  addMessage,
  StoredReceivedChatMessage,
  EMPTY_LIST,
  addToWhatsappGroup as addToWhatsappGroupApi
} from './CommunicationAPI';
import { CapacitorStorage } from '../../app/persist';
import { Capacitor } from '@capacitor/core';
import storage from 'redux-persist/lib/storage';

type RequestId = number;

export interface CommunicationState {
  status: 'idle' | 'loading' | 'failed';
  errors: FixedQueue<ErrorRequestResult>;
  last5Sent: FixedQueue<RequestId>;
  lastReceivedTimestamp: Record<RoomId, number>;
  lastTimestamps: Record<RoomId, number>;
  unreadChats: RoomId[];
  roomIdsPerProject: Record<string, RoomId[]>;
  lastSeen: Record<RoomId, number>;
  pickedFileForChat: ProjectEntityRef | null;
  myRooms: Room[] | null;
  roomsMembers: Record<RoomId, ChatAlias[]>;
  chatConnected: boolean;
  connectionSetted: boolean;
  store: MessagesStorage;
  lastDraft?: {
    room: RoomId,
    message: string
  },
}

const initialState: CommunicationState = {
  status: 'idle',
  errors: { size: 5, elements: [] },
  last5Sent: { size: 5, elements: [] },
  lastReceivedTimestamp: {},
  lastTimestamps: {},
  unreadChats: [],
  roomIdsPerProject: {},
  lastSeen: {},
  pickedFileForChat: null,
  myRooms: null,
  roomsMembers: {},
  chatConnected: false,
  connectionSetted: false,
  store: {},
};

type SendRequest ={
  id: RequestId;
  token: string;
}

type BaseRequestResult = {
  id: RequestId;
}
type SuccessfulRequestResult = BaseRequestResult & {
  result: true;
}
type ErrorRequestResult = BaseRequestResult & {
  result: false;
  error: Error;
}
type RequestResult = SuccessfulRequestResult | ErrorRequestResult;

export type ChatOpeningResult = RequestResult & {
  socket: Socket;
}

export const openChatConnection = createAsyncThunk<ChatOpeningResult, SendRequest>(
  'communication/open_chat_connection',
  async ({ id, token }, { rejectWithValue, dispatch }) => {
    try{
      const socket = await openChatConnectionAPI(
        token,
        () => dispatch(chatConnected()),
        () => dispatch(chatDisconnected()),
      );
      return { id, result: true, socket };
    }catch(error){
      return rejectWithValue({
        id, result: false, error
      });
    }
  }
);

export const getUsersByChatId = createAsyncThunk(
  'communication/get_users_by_chat_id',
  async ({chatid, token}: {chatid: string; token: string}, { rejectWithValue, dispatch }) => {
    try{
      let group = RoomManager.extractFromRoomId(chatid) as ServerRoomId | undefined;
      if(!group) throw new Error("Chat non disponibile");
      if(!(group instanceof Array)) group = [group];
      let members: ChatAlias[] = [];
      for(const userIdentity of group){
        if(userIdentity.indexOf('@') > -1)
          members.push(
            new ChatUser(await dispatch(
              getUser({ id: userIdentity, token })
            ).then(unwrapResult))
          )
        else if(userIdentity.startsWith('group:')){
          const groups = await dispatch(loadGroups({ token })).then(unwrapResult)
          const groupRef = userIdentity.substring(6);
          const group =
            groups.find(group => `${group.id}` === groupRef) ||
            groups.find(group => group.name === groupRef);
          if(group)
            members.push(new ChatGroup(group));
        }
        else if(userIdentity.startsWith('project:')){
          const projectId = userIdentity.substring(8);
          const project = (await dispatch(loadMine({ token })).then(unwrapResult))
            .find(project => project.id === projectId);
          if(project)
            members.push(new ChatProject(project));
        }
      }
      return { chatid, members }
    }catch(error){
      return rejectWithValue(error);
    }
  }
);

export const addToWhatsappGroup = createAsyncThunk(
  'communication/add_to_wa_group',
  async (
    {room, telephone, token}: { room: RoomId, telephone: string; token: string },
    { rejectWithValue, getState }
  ) => {
    let error: unknown
    try{
      const result = await addToWhatsappGroupApi(token, telephone, room)
      if (result !== null) {
        if (result && result.code === "WHATSAPP_ADD_USER_TO_GROUP_FAILURE") {
          error = new Error("Need to add contact")
        } else return
      } else {
        error = new Error("Rejected request")
      }
    }catch(e){
      error = e
    }
    return rejectWithValue({
      id: 0, result: false, error
    });
  }
);

export type ProjectEntityRef = {
  entity: ProjectEntity;
  location: string;
}

export const communicationSlice = createSlice({
  name: 'communication',
  initialState,
  reducers: {
    chatReconnect: () => {},
    fetchChatMessages: (_, __: PayloadAction<{
      id: number;
      fromDate: number;
      limit: number;
      room: RoomId;
      bufferIfConnectionNotReady?: boolean;
    }>) => {},
    initRoom: (_, __: PayloadAction<ServerRoom>) => {},
    setDraft: (state, action: PayloadAction<{ room: RoomId, message: string }>) => {
      if (!action.payload.message)
        state.lastDraft = undefined
      else
        state.lastDraft = action.payload
    },
    removeError: (state, action: PayloadAction<RequestId>) => {
      state.errors = removeFromFixedQueue(
        error => error.id !== action.payload, state.errors
      )
    },
    removeSent: (state, action: PayloadAction<RequestId>) => {
      state.last5Sent = removeFromFixedQueue(
        sent => sent !== action.payload, state.last5Sent
      )
    },
    receivedMessage: (state, action: PayloadAction<ReceivedChatMessage>) => {
      const message: StoredReceivedChatMessage = {...action.payload, checkTimestamp: Date.now()}
      state.store = addMessage(state.store, message)
      const roomId = RoomManager.generateRoomId(message.room);
      state.lastReceivedTimestamp[roomId] = message.timestamp;
      if(
        !state.lastTimestamps[roomId] ||
        message.timestamp > state.lastTimestamps[roomId]
      ){
        state.lastTimestamps[roomId] = message.timestamp;
      }
      if(
        state.lastSeen[roomId] &&
        state.lastTimestamps[roomId] > state.lastSeen[roomId] &&
        !state.unreadChats.includes(roomId)
      )
        state.unreadChats.push(roomId);
    },
    saw: (
      state, action: PayloadAction<{ room: RoomId, timestamp: number }>
    ) => {
      const { room, timestamp } = action.payload;
      if (state.lastSeen[room] > 17489040804000) {
        state.lastSeen[room] = Math.floor(state.lastSeen[room]/1000)
      }
      if(!state.lastSeen[room] || timestamp > state.lastSeen[room])
        state.lastSeen[room] = timestamp;
      if(
        state.lastSeen[room] >= state.lastTimestamps[room] &&
        state.unreadChats.includes(room)
      )
        state.unreadChats = state.unreadChats.filter(chats => chats !== room);
    },
    addRoom: (state, action: PayloadAction<{ room: ServerRoom }>) => {
      const serverRoom = action.payload.room;
      const roomId = RoomManager.generateRoomId(serverRoom.id);
      const participants = serverRoom.id instanceof Array ? serverRoom.id : [serverRoom.id];
      if(participants.find(participant => participant.startsWith('project:'))){
        const projectIds = participants.filter(
          participant => participant.startsWith('project:')
        ).map(
          participant => participant.substring(8)
        );
        for(const projectId of projectIds){
          if(!state.roomIdsPerProject[projectId])
            state.roomIdsPerProject[projectId] = [];
          state.roomIdsPerProject[projectId].push(roomId);
        }
      }
      if(state.myRooms === null) state.myRooms = [];
      const room: Room = {
        id: roomId,
        lastUpdateTimestamp:
          serverRoom.lastUpdateTimestamp ?
          serverRoom.lastUpdateTimestamp * 1000 :
          undefined
      }
      if(!state.myRooms.find(savedRoom => savedRoom.id === roomId)){
        state.myRooms = [...state.myRooms, room];
      }
      if(
        room.lastUpdateTimestamp &&
        (
          !state.lastSeen[roomId] ||
          room.lastUpdateTimestamp > state.lastSeen[roomId]
        ) &&
        !state.unreadChats.includes(roomId)
      )
        state.unreadChats.push(roomId);
    },
    removeRoom: (state, action: PayloadAction<ServerRoomId>) => {
      const serverRoom = action.payload;
      const room = RoomManager.generateRoomId(serverRoom);
      state.myRooms = state.myRooms?.filter(savedRoom => savedRoom.id === room) || null;
    },
    pickFileForChat: (state, action: PayloadAction<ProjectEntityRef>) => {
      state.pickedFileForChat = action.payload;
    },
    clearPickedForChat: (state) => {
      state.pickedFileForChat = null;
    },
    chatConnected: (state) => {
      state.chatConnected = true;
    },
    chatDisconnected: (state) => {
      state.chatConnected = false;
    },
    sendChatMessage: (state, action: PayloadAction<
      SendRequest & {
        room: RoomId;
        message: ChatMessageRequest;
      }
    >) => {
      state.status = 'idle';
      const { id, room, message } = action.payload;
      let metadata;
      if(message?.attachment){
        metadata = {
          attachment: message.attachment
        };
      }
      state.last5Sent = pushInFixedQueue(id, state.last5Sent)
      if (room && (message?.content || message?.attachment)) {
        const messageStub: ReceivedChatMessage = {
          stub: true,
          room,
          id: Date.now().toString(),
          timestamp: Math.round(Date.now() / 1000),
          content: message?.content,
          metadata
        }
        state.store = addMessage(state.store, messageStub as ReceivedChatMessage);
        const roomId = RoomManager.generateRoomId(room);
        state.lastReceivedTimestamp[roomId] = Date.now();
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        logout.fulfilled,
        (state) => { state.store = {} }
      )
      .addCase(
        openChatConnection.fulfilled,
        (state) => {
          state.status = 'idle';
          state.connectionSetted = true
        }
      )
      .addCase(
        getUsersByChatId.fulfilled,
        (state, action: PayloadAction<{ chatid: RoomId, members: ChatAlias[] }>) => {
          state.status = 'idle';
          const { chatid, members } = action.payload;
          state.roomsMembers[chatid] = members;
        }
      )
      .addCase(
        openChatConnection.pending, (state) => {
          state.chatConnected = false;
        }
      )
      .addMatcher(
        isAnyOf(
          openChatConnection.pending,
          getUsersByChatId.pending
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isAnyOf(
          openChatConnection.rejected,
        ),
        (state, action) => {
          state.status = 'failed';
          const result = action.payload as ErrorRequestResult;
          state.errors = pushInFixedQueue(result, state.errors)
        }
      )
      .addMatcher(
        isAnyOf(initEnvironment.fulfilled),
        (state) => {
          state.last5Sent = { size: 5, elements: [] };
          state.lastReceivedTimestamp = {};
          state.lastTimestamps = {};
          state.roomIdsPerProject = {};
          state.unreadChats = [];
          state.pickedFileForChat = null;
          state.myRooms = null;
          state.roomsMembers = {};
          state.store = {}
        }
      )
      .addMatcher(
        isAnyOf(
          openChatConnection.fulfilled,
          getUsersByChatId.fulfilled
        ), (state) => {
          state.status = 'idle';
        }
      );
  },
});

export const {
  removeError, removeSent, receivedMessage, addRoom, removeRoom, setDraft, initRoom, fetchChatMessages, chatReconnect, sendChatMessage
} = communicationSlice.actions;

export const isLoading = (state: RootState) => state.communication.status === 'loading';
export const getError =
(requestId: RequestId) =>
  (state: RootState) => state.communication.errors.elements.find(error => error.id === requestId);
export const isSent =
  (requestId: RequestId) =>
    (state: RootState) => state.communication.last5Sent.elements.includes(requestId);
export const isChatConnected =
  (state: RootState) =>
    state.communication.chatConnected &&
    state.communication.connectionSetted;
export const isChatDisconnected = (state: RootState) => !state.communication.chatConnected;
export const getMessages =
  (recipient: RoomId) =>
    (state: RootState) => state.communication.store[recipient] || EMPTY_LIST;
export const getLastReceivedTimestamp =
  (recipient: string) =>
    (state: RootState) =>
      state.communication.lastReceivedTimestamp[
        RoomManager.generateRoomId(recipient)
      ];
export const getPickedFileForChat =
  (state: RootState) => state.communication.pickedFileForChat;
export const getMyRooms = (state: RootState) => state.communication.myRooms;
export const isUserInRoom =
  (roomId: RoomId) =>
    (state: RootState) =>
      Boolean(state.communication.myRooms?.find(room => room.id === roomId))
export const getRoomLastUpdateTime =
  (roomId: RoomId) =>
    (state: RootState) =>
      state.communication.myRooms?.find(
        room => room.id === roomId
      )?.lastUpdateTimestamp;
export const getUnreadChatsProject = (projectId: string) => (state: RootState) => {
  const roomIds = state.communication.roomIdsPerProject[projectId] || [];
  return roomIds.filter(
    roomId => state.communication.unreadChats.includes(roomId)
  );
}
export const getUnreadChatCountOfProject = (projectId: string) => (state: RootState) => {
  const unreadRooms = getUnreadChatsProject(projectId)(state);
  return unreadRooms.length;
};
export const isChatUnread = (room: RoomId) => (state: RootState) => {
  return state.communication.unreadChats.includes(room);
};
export const getDraft = (room: RoomId) => (state: RootState) => {
  return state.communication.lastDraft?.room === room
    ? state.communication.lastDraft.message
    : null
};

class ChatAlias{
  details: any;
  constructor(details: any){
    this.details = details;
  }
  getName(): string{
    return '';
  }
}
export class ChatUser extends ChatAlias{
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(details: UserDetails){
    super(details)
  }
  getName() {
    return (this.details as UserDetails).name;
  }
}
export class ChatGroup extends ChatAlias{
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(details: Group){
    super(details)
  }
  getName() {
    return (this.details as Group).name;
  }
}
export class ChatProject extends ChatAlias{
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(details: Project){
    super(details)
  }
  getName() {
    return 'Tutto il team';
  }
}
export const getLoadedUsersByChatId =
  (chatid: string) => (state: RootState) => state.communication.roomsMembers[chatid];

export const chatMiddleware: () => Middleware<any, any, Dispatch<AnyAction>> = () => {
  let socket: Socket | null = null
  return store => next => action => {
    if(openChatConnection.fulfilled.match(action)){
      if (socket) socket.disconnect()
      socket = (action.payload as ChatOpeningResult).socket;
      socket.on('message', (message) => {
        console.debug("Message received", message)
        store.dispatch(receivedMessage(message));
      })
      socket.on('add-room', (room: ServerRoom) => {
        console.debug("Room joined", room)
        store.dispatch(initRoom(room) as unknown as AnyAction);
      })
      socket.on('exit-room', (roomId) => {
        console.debug("Room exited", roomId)
        store.dispatch(removeRoom(roomId));
      })
      socket.on('disconnect', (error) => {
        if (!socket?.active) {
          console.debug("Chat disconnected", error)
          store.dispatch(chatDisconnected())
        }
      })
      socket.emit('get-rooms');
    } else if (chatReconnect.match(action)) {
      socket?.on("connect", () => store.dispatch(chatConnected()));
      socket?.on("connect_error", () => store.dispatch(chatDisconnected()));
      socket?.connect();
    } else if (logout.fulfilled.match(action)) {
      RoomManager.virtualGroupsMap = {}
      socket?.close()
    } else if (initRoom.match(action)) {
      const room = action.payload
      const roomId = RoomManager.generateRoomId(room.id);
      store.dispatch(addRoom({ room }))
      const state = store.getState()
      if (
        socket &&
        room.lastUpdateTimestamp &&
        state.communication.lastSeen[roomId] < (room.lastUpdateTimestamp*1000)
      )
        fetchChatMessagesAPI(
          Date.now(),
          30,
          roomId,
          socket
        );
    } else if (sendChatMessage.match(action)) {
      const { room, message } = action.payload
      if (room && message && socket) {
        const serverRoomId = RoomManager.extractFromRoomId(room)
        if (serverRoomId)
          sendChatMessageAPI(serverRoomId, message, socket)
      }
    } else if (fetchChatMessages.match(action)) {
      const { fromDate, limit, room, bufferIfConnectionNotReady } = action.payload
      if (socket)
        fetchChatMessagesAPI(
          fromDate,
          limit,
          room,
          socket,
          bufferIfConnectionNotReady
        );
    }
    return next(action)
  }
}

export const {
  saw, pickFileForChat, clearPickedForChat, chatConnected, chatDisconnected
} = communicationSlice.actions;

const MessagesStoreTransform = createTransform(
  (inboundState: MessagesStorage) => {
    const messagesCache: any = {};
    for (const key in inboundState) {
      const value = inboundState[key];
      messagesCache[key] = value.slice(-100)
    }
    return messagesCache;
  },
  (outboundState: MessagesStorage) => outboundState,
  { whitelist: ['store'] }
);

const persistConfig = {
  key: 'communication',
  storage: CapacitorStorage,
  transforms: [MessagesStoreTransform],
  whitelist: ['lastSeen', 'store', 'lastDraft'],
};

const webPersistConfig = {
  key: 'communication',
  storage: storage,
  whitelist: ['lastSeen', 'lastDraft'],
};

export default (
  Capacitor.isNativePlatform()
    ? persistReducer<CommunicationState>(persistConfig, communicationSlice.reducer)
    : persistReducer<CommunicationState>(webPersistConfig, communicationSlice.reducer)
  );
